import React from "react";
import { Link } from "react-router-dom";
import { MapPin, Phone, Mail, MessageCircle, Globe, Instagram, Facebook, Youtube, Twitter } from "lucide-react";
import { BUSINESS } from "@/data/site";

const wa = (n) =>
  `https://wa.me/91${n}?text=${encodeURIComponent("Hello Chakri Travels, I want to book a car rental service.")}`;

const SOCIAL = [
  { key: "instagram", label: "Instagram", Icon: Instagram },
  { key: "facebook", label: "Facebook", Icon: Facebook },
  { key: "youtube", label: "YouTube", Icon: Youtube },
  { key: "twitter", label: "Twitter / X", Icon: Twitter },
];

const Footer = () => {
  const social = SOCIAL.filter((s) => BUSINESS.social && BUSINESS.social[s.key]);

  return (
    <footer data-testid="footer" className="gradient-royal text-white mt-0">
      <div className="mx-auto max-w-7xl px-5 lg:px-8 py-14 grid md:grid-cols-4 gap-10">
        <div className="md:col-span-2">
          <div className="flex items-center gap-3">
            <div className="brand-logo-box is-gold">
              {BUSINESS.logo ? (
                <img src={BUSINESS.logo} alt="Chakri Travels logo" />
              ) : (
                <span className="font-display text-[var(--royal-deep)] font-bold text-xl">C</span>
              )}
            </div>
            <div className="leading-tight">
              <p className="font-display text-white text-xl font-semibold">
                Chakri <span className="italic text-[var(--gold)]">Travels</span>
              </p>
              <p className="text-[11px] font-semibold uppercase tracking-[0.2em] text-[var(--gold)]">
                {BUSINESS.tagline}
              </p>
            </div>
          </div>

          <p className="text-white/75 text-sm mt-5 max-w-md leading-relaxed">
            Premium chauffeur-driven car rental & travel services across Kakinada, Rajamahendravaram, Narasapuram, Amalapuram and all of Andhra Pradesh.
          </p>
          <p className="text-white/90 text-sm mt-5 flex items-start gap-2">
            <MapPin size={16} className="text-[var(--gold)] mt-0.5 flex-shrink-0" /> {BUSINESS.address}
          </p>
          <a href={`https://${BUSINESS.domain}`} className="mt-3 inline-flex items-center gap-2 text-white/85 hover:text-[var(--gold)] text-sm">
            <Globe size={14} className="text-[var(--gold)]" /> {BUSINESS.domain}
          </a>

          {social.length > 0 && (
            <div className="mt-6">
              <p className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] mb-3 font-semibold">Follow us</p>
              <div className="flex flex-wrap gap-2.5" data-testid="footer-social">
                {social.map(({ key, label, Icon }) => (
                  <a
                    key={key}
                    href={BUSINESS.social[key]}
                    target="_blank"
                    rel="noreferrer"
                    aria-label={label}
                    data-testid={`social-${key}`}
                    className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/20 text-white hover:bg-[var(--gold)] hover:text-[var(--royal-deep)] transition-colors"
                  >
                    <Icon size={18} />
                  </a>
                ))}
              </div>
            </div>
          )}
        </div>

        <div>
          <p className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] mb-4 font-semibold">Contact</p>
          {BUSINESS.phones.map((p) => (
            <a key={p} href={`tel:+91${p}`} className="flex items-center gap-2 text-white/85 hover:text-[var(--gold)] text-sm mb-2">
              <Phone size={14} /> +91 {p}
            </a>
          ))}
          <a href={`mailto:${BUSINESS.email}`} className="flex items-center gap-2 text-white/85 hover:text-[var(--gold)] text-sm mt-1">
            <Mail size={14} /> {BUSINESS.email}
          </a>
          <a href={wa(BUSINESS.primaryWhatsApp)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white/85 hover:text-[var(--gold)] text-sm mt-3">
            <MessageCircle size={14} /> WhatsApp · {BUSINESS.primaryWhatsApp}
          </a>
          <a href={wa(BUSINESS.secondaryWhatsApp)} target="_blank" rel="noreferrer" className="flex items-center gap-2 text-white/85 hover:text-[var(--gold)] text-sm">
            <MessageCircle size={14} /> WhatsApp · {BUSINESS.secondaryWhatsApp}
          </a>
        </div>

        <div>
          <p className="text-[var(--gold)] text-xs uppercase tracking-[0.2em] mb-4 font-semibold">Quick Links</p>
          {[
            { l: "Home", h: "/" },
            { l: "About", h: "/about" },
            { l: "Services", h: "/services" },
            { l: "Contact", h: "/contact" },
          ].map((l) => (
            <Link key={l.l} to={l.h} className="block text-white/85 hover:text-[var(--gold)] text-sm mb-2">
              {l.l}
            </Link>
          ))}
        </div>
      </div>

      <div className="border-t border-white/10 py-5 text-center text-white/60 text-xs">
        © {new Date().getFullYear()} Chakri Travels · {BUSINESS.domain} · Kakinada, Andhra Pradesh · All rights reserved.
      </div>
    </footer>
  );
};

export default Footer;
